import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  ChevronDown,
  ChevronUp,
  MapPin,
  Truck,
  Gift,
  User,
  Phone,
  Mail,
  SquarePen,
} from "lucide-react";
import "../styles/checkout.scss";
import { useAuth } from "../context/userContext";
import {
  addNewUserAddress,
  clearErrors,
  clearUpdateState,
  updateUserAddress,
} from "../redux/action/userAction";
import { useDispatch, useSelector } from "react-redux";
import { useNotification } from "../context/notificationContext";
import {
  clearOrderPaymentError,
  clearOrderPaymentState,
  orderPaymentAction,
} from "../redux/action/orderAction";
import { useCart } from "../context/CartContext";
import { useLocation, useNavigate } from "react-router-dom";
import userServiceInstance from "../services/userService";
import moment from "moment/moment";
import ButtonLoader from "../components/reusable/ButtonLoader";
import CustomInput from "../components/reusable/CustomInput";
import CustomButton from "../components/reusable/CustomButton";
import { axiosInstance } from "../utils/config";
import { toast } from "react-toastify";
import { useCheckout } from "../context/CheckoutContext";

const Checkout = () => {
  const { auth, setAuth, loading: profileLoading } = useAuth();
  const { setCheckoutModal } = useCheckout();
  const {setCartShow} = useCart()
  const [, , showNotification] = useNotification();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [addAddress, setAddAddress] = useState(false);
  const [selectAddress, setSelectAddress] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [payItems, setPayItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(true);
  const [showAvailableCoupons, setShowAvailableCoupons] = useState(false);
  const [paymentType, setPaymentType] = useState("cod");
  const [buyproduct, setBuyproduct] = useState(null);
  const [buySize, setBuySize] = useState(null);
  const [lastCheckedCode, setLastCheckedCode] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const { clearCart, cartList: cartItems } = useCart();

  const [addressDetails, setAddressDetails] = useState({
    fullAddress: "",
    city: "",
    postalCode: "",
    state: "",
    country: "",
    addressPhone: "",
    recieverName: "",
    email: "",
  });

  useEffect(() => {
    if (auth?.user?.addresses?.length > 0) {
      setSelectAddress(auth?.user?.addresses[0]?._id);
    }
  }, [auth]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const active = queryParams.get("buyId");
    const size = queryParams.get("sz");
    if (size) {
      setBuySize(size);
    }
    if (active) {
      setBuyproduct(active);
    }
  }, [location]);

  useEffect(() => {
    if (buyproduct !== null) {
      let filterData = cartItems?.filter(
        (itm) => itm?.productId === buyproduct
      );

      if (buySize) {
        filterData = filterData?.filter(
          (itm) => itm?.size?.trim() === buySize?.trim()
        );
      }
      if (filterData?.length > 0) {
        setAllItems(filterData);
        setTotalAmount(
          filterData?.reduce(
            (total, item) => total + item.price * item.quantity,
            0
          )
        );
      } else {
        setAllItems(cartItems);
        setTotalAmount(
          cartItems?.reduce(
            (total, item) => total + item.price * item.quantity,
            0
          )
        );
      }
    } else {
      setAllItems(cartItems);
      setTotalAmount(
        cartItems?.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        )
      );
    }
  }, [buyproduct, buySize, cartItems]);

  useEffect(() => {
    if (allItems) {
      const transformedPayItems = allItems?.map((item) => ({
        id: item?.productId,
        quantity: item?.quantity,
        varient: item?.size,
      }));
      if (allItems?.length > 0) {
        window.fbq("track", "InitiateCheckout", {
          content_ids: allItems.map((i) => i?.productId),
          value: totalAmount,
          currency: "INR",
          num_items: allItems.length,
        });
      }
      setPayItems(transformedPayItems);
      setIsCouponApplied(false);
      setCouponCode("");
      setDiscountAmount(0);
    }
  }, [cartItems, allItems, totalAmount]);

  // ADD ADDRESS FUNCTION STARTS FROM HERE

  const handleAddAddress = () => {
    setAddAddress(true);
    setEditAddress(false);
  };

  const [editAddress, setEditAddress] = useState(false);
  const [editAddressId, setEditAddressId] = useState(false);
  const handleEditAddress = (details) => {
    setEditAddress(true);
    setAddAddress(false);
    setEditAddressId(details?._id);
    setAddressDetails({
      addressPhone: details?.addressPhone,
      fullAddress: details?.fullAddress,
      recieverName: details?.fullName,
      postalCode: details?.postalCode,
      state: details?.state,
      country: details?.country,
      city: details?.city,
      email: details?.addressEmail,
    });
  };

  // UPDATE ADDRESS FUNCTION STARTS
  const {
    error,
    loading: updateProfileLoading,
    userInfo: updateAddressInfo,
  } = useSelector((state) => state.updateProfile);

  const handleUpdateAddress = async () => {
    const newErrors = {};
    Object.keys(addressDetails).forEach((key) => {
      const error = validateField(key, addressDetails[key], addressDetails);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      showNotification("error", newErrors[firstErrorField], {
        id: "form-error",
      });
      return;
    }

    dispatch(
      updateUserAddress(
        email,
        recieverName,
        addressPhone,
        fullAddress,
        postalCode,
        state,
        country,
        city,
        editAddressId
      )
    );
  };

  useEffect(() => {
    if (error) {
      showNotification("error", error);
      dispatch(clearErrors());
    }
    if (updateAddressInfo?.success) {
      toast.success(updateAddressInfo?.message);
      setEditAddress(false);
      setAddressDetails({
        id: "",
        fullAddress: "",
        city: "",
        postalCode: "",
        state: "",
        country: "",
        addressPhone: "",
        recieverName: "",
      });
      fetchProfile();
      dispatch(clearUpdateState());
    }
  }, [error, showNotification, dispatch, updateAddressInfo]);
  // UPDATE ADDRESS FUNCTION ENDS

  const {
    addressPhone,
    fullAddress,
    recieverName,
    postalCode,
    state,
    country,
    city,
    email,
  } = addressDetails;

  const {
    error: addressError,
    loading: addressLoading,
    userInfo,
  } = useSelector((state) => state.addNewAddress);

  // Custom validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validatePincode = (pincode) => {
    const pincodeRegex = /^[1-9][0-9]{5}$/;
    return pincodeRegex.test(pincode);
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await response.json();

      if (data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];
        setAddressDetails((prev) => ({
          ...prev,
          state: postOffice.State,
          city: postOffice.District,
          country: "India",
        }));
      } else {
        showNotification("error", "Invalid Pincode");
      }
    } catch (error) {
      showNotification("error", "Error fetching pincode details");
    }
  };

  const validateField = (name, value, allFields) => {
    const emptyFields = Object?.keys(allFields).filter(
      (key) => !allFields[key] || allFields[key].toString().trim() === ""
    );

    if (emptyFields.length > 1) {
      return "Please fill in all fields";
    }

    switch (name) {
      case "email":
        return validateEmail(value) ? "" : "Invalid email address";
      case "addressPhone":
        return validatePhone(value) ? "" : "Invalid phone number";
      case "postalCode":
        return validatePincode(value) ? "" : "Invalid pincode";
      case "recieverName":
        return value.trim().length >= 2 ? "" : "Name too short";
      case "fullAddress":
        return value.trim().length >= 10 ? "" : "Address too short";
      default:
        return "";
    }
  };

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "postalCode") {
      processedValue = value.replace(/\D/g, "").slice(0, 6);

      setAddressDetails((prev) => ({
        ...prev,
        [name]: processedValue,
      }));

      if (processedValue.length === 6 && validatePincode(processedValue)) {
        fetchPincodeDetails(processedValue);
      }
    } else {
      setAddressDetails((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
    }
  };

  const postalCodeInputRef = useRef();
  const handlePostalCodeBlur = () => {
    const pincode = postalCodeInputRef.current.value;

    if (pincode?.length === 6 && validatePincode(pincode)) {
      fetchPincodeDetails(pincode);
    } else if (pincode?.length > 0) {
      showNotification("error", "Please enter a valid 6-digit pincode");
    }
  };

  const handleSaveAddress = () => {
    const newErrors = {};
    Object.keys(addressDetails).forEach((key) => {
      const error = validateField(key, addressDetails[key], addressDetails);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      showNotification("error", newErrors[firstErrorField], {
        id: "form-error",
      });
      return;
    }
    dispatch(
      addNewUserAddress(
        email,
        recieverName,
        addressPhone,
        fullAddress,
        postalCode,
        state,
        country,
        city
      )
    );
  };

  useEffect(() => {
    if (addressError) {
      showNotification("error", addressError);
      dispatch(clearErrors());
    }
    if (userInfo?.success) {
      showNotification("success", userInfo?.message);
      fetchProfile();
      setAddAddress(false);
      dispatch(clearUpdateState());
    }
  }, [dispatch, showNotification, userInfo, addressError]);

  //  ADD ADDRESS FUNCTION ENDS HERE

  const fetchAvailableCoupons = async () => {
    setCouponsLoading(true);
    try {
      const res = await userServiceInstance.getAvailableCoupons();
      if (res?.data?.success) {
        setAvailableCoupons(res?.data?.coupons);
      }
    } catch (error) {
      showNotification("error", "Failed to load coupons");
    } finally {
      setCouponsLoading(false);
    }
  };

  const handleViewCoupons = () => {
    if (!showAvailableCoupons) {
      fetchAvailableCoupons();
    }
    setShowAvailableCoupons(!showAvailableCoupons);
  };

  const isCouponApplicable = (coupon) => {
    if (!coupon?.isActive) return false;
    if (coupon?.minPurchaseValue && totalAmount < coupon?.minPurchaseValue)
      return false;
    if (coupon?.usageLimit && coupon?.usedCount >= coupon?.usageLimit)
      return false;
    if (coupon?.toDate && new Date() > new Date(coupon?.toDate)) return false;
    return true;
  };

  const [couponLoading, setCouponLoading] = useState(false);

  const handleApplyCoupon = async () => {
    window.fbq("track", "couponCheck", {
      content_ids: payItems,
      total: totalAmount,
    });
    if (auth?.user?.addresses?.length < 1) {
      showNotification(
        "error",
        "Please update your address before applying coupon"
      );
      return;
    }
    setCouponLoading(true);
    if (auth?.user?.coupon?.code?.toLowerCase() === couponCode?.toLowerCase()) {
      setCouponError("You can't apply your self code.");
      return;
    }
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code.");
      setIsCouponApplied(false);
      setLastCheckedCode(couponCode);
      return;
    }
    try {
      const encodeCode = encodeURIComponent(couponCode);
      const res = await userServiceInstance.getCouponValidity(
        encodeCode,
        totalAmount,
        paymentType
      );
      setLastCheckedCode(couponCode);
      if (res?.data?.success) {
        const percent = res.data.data.discountPercent;
        const discount = (totalAmount * percent) / 100;
        setDiscountAmount(discount);
        setCouponError("");
        setIsCouponApplied(true);
        setCouponLoading(false);
      } else {
        setDiscountAmount(0);
        setCouponError(res?.data?.message);
        setIsCouponApplied(true);
        setCouponLoading(false);
      }
    } catch (error) {
      setDiscountAmount(0);
      setCouponError(error?.response?.data?.message);
      setLastCheckedCode(couponCode);
      setIsCouponApplied(true); // Disable button until change
      setCouponLoading(false);
    }
  };

  const {
    error: paymentError,
    loading: paymentLoading,
    paymentInfo,
  } = useSelector((state) => state.createOrderPayment);

  useEffect(() => {
    if (paymentError) {
      showNotification("error", paymentError);
      dispatch(clearOrderPaymentError());
    }
    if (paymentInfo?.success) {
      window.fbq("track", "Purchase", {
        content_ids: payItems?.map((i) => i.id),
        value: totalAmount - discountAmount + (totalAmount <= 999 ? 149 : 0),
        currency: "INR",
      });
      if (paymentInfo?.data?.instrumentResponse?.redirectInfo?.url) {
        window.open(
          paymentInfo?.data?.instrumentResponse?.redirectInfo?.url,
          "_self"
        );
        dispatch(clearOrderPaymentState());
      }
      if (paymentType === "cod") {
        clearCart();
        dispatch(clearOrderPaymentState());
        setCartShow(false);
        setCheckoutModal(false);
        navigate("/account/payment-success");
        setPayItems([]);
      }
    }
  }, [
    paymentError,
    showNotification,
    paymentType,
    navigate,
    clearCart,
    paymentInfo,
    totalAmount,
    discountAmount,
    payItems,
    dispatch,
    setCheckoutModal
  ]);

  const handlePlaceOrder = () => {
    const paymentMethod = paymentType === "cod" ? "COD" : "ONLINE";
    dispatch(
      orderPaymentAction(
        payItems,
        selectAddress,
        paymentMethod,
        isCouponApplied && couponError === "" ? couponCode?.toUpperCase() : ""
      )
    );
  };

  const finalAmount = totalAmount - discountAmount;
  const deliveryCharge = finalAmount <= 999 ? 149 : 0;
  const payableAmount = finalAmount + deliveryCharge;

  const getStatusText = (coupon, isApplicable) => {
    if (isApplicable) return "Available";
    if (!coupon.isActive) return "Inactive";

    const today = new Date();
    const expiryDate = new Date(coupon.toDate);
    if (expiryDate < today) return "Expired";

    if (coupon.minPurchaseValue && totalAmount < coupon.minPurchaseValue) {
      return `Add ₹${coupon.minPurchaseValue - totalAmount} more`;
    }

    if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit) {
      return "Usage limit reached";
    }

    return "Expired";
  };

  // LOGIN USER LOGIC START FROM HERE BY RAHUL

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (otpSent) {
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
  }, [otpSent]);

  const handleSendOtp = async () => {
    if (mobile.trim().length === 10) {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_LIVE}/request-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              mobileNo: mobile,
            }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setOtp(new Array(6).fill(""));
          setOtpSent(true);
          showNotification("success", "OTP sent successfully");
        } else {
          showNotification("error", data.message || "Failed to send OTP");
        }
      } catch (error) {
        showNotification("error", "Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      showNotification("error", "Please enter a valid 10 digit mobile number");
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_LIVE}/resend-otp/${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response?.success) {
        setOtp(new Array(6).fill(""));
        showNotification("success", "OTP resent successfully");
        setTimeout(() => inputsRef.current[0]?.focus(), 50);
      } else {
        showNotification("error", data.message || "Failed to resend OTP");
      }
    } catch (error) {
      showNotification("error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    const nextOtp = [...otp];

    if (!val) {
      nextOtp[idx] = "";
      setOtp(nextOtp);
      return;
    }

    if (val.length === 1) {
      nextOtp[idx] = val;
      setOtp(nextOtp);
      if (idx < 5) inputsRef.current[idx + 1]?.focus();
    } else {
      const digits = val.split("");
      for (let i = 0; i < digits.length && idx + i < 6; i++) {
        nextOtp[idx + i] = digits[i];
      }
      setOtp(nextOtp);
      const focusIndex = Math.min(5, idx + digits.length);
      inputsRef.current[focusIndex]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const nextOtp = [...otp];

      if (otp[idx]) {
        nextOtp[idx] = "";
        setOtp(nextOtp);
        inputsRef.current[idx]?.focus();
      } else if (idx > 0) {
        nextOtp[idx - 1] = "";
        setOtp(nextOtp);
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("Text").replace(/\D/g, "");
    if (!paste) return;
    const nextOtp = [...otp];
    for (let i = 0; i < paste.length && idx + i < 6; i++) {
      nextOtp[idx + i] = paste[i];
    }
    setOtp(nextOtp);
    const focusIndex = Math.min(5, idx + paste.length);
    inputsRef.current[focusIndex]?.focus();
  };

  const fetchProfile = async () => {
    setAuth((prevState) => ({
      ...prevState,
      loading: false,
      error: null,
    }));

    await axiosInstance
      .get("/me", { withCredentials: true })
      .then((data) => {
        setAuth({
          user: data?.data?.user,
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        setAuth((prevState) => ({
          ...prevState,
          loading: false,
          error:
            error?.response?.data?.message === "Not logged in"
              ? null
              : error?.response?.data?.message,
        }));
      });
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      showNotification("error", "Please enter the full 6 digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_LIVE}/verify-otp/${mobile}/${code}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (response?.ok) {
        await fetchProfile();
      } else {
        showNotification("error", response?.message || "Invalid OTP");
      }
    } catch (error) {
      showNotification("error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-checkout">
      <div
        className="mobile-summary-header"
        onClick={() => setShowOrderSummary(!showOrderSummary)}
      >
        <div className="summary-info">
          <strong className="summary_heading">Order Summary</strong>
        </div>
        <div className="total-amount">
          {showOrderSummary ? (
            <ChevronUp size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
        </div>
      </div>
      <div
        className="mobile-summary-header"
        onClick={() => setShowOrderSummary(!showOrderSummary)}
      >
        <div className="summary-info">
          {discountAmount && discountAmount > 0 ? (
            <span className="savings">₹{discountAmount} saved so far</span>
          ) : (
            ""
          )}{" "}
          <span className="item-count">{allItems?.length} items</span>
        </div>
        <div className="total-amount">₹{payableAmount}</div>
      </div>

      <div className={`order-summary-mobile ${showOrderSummary ? "show" : ""}`}>
        <div className="summary-content">
          {allItems?.map((item) => (
            <div key={item._id} className="summary-item">
              <img
                src={process.env.REACT_APP_FILE_PATH + "/" + item?.image}
                alt={item.name}
                className="item-image"
              />

              <div className="item-details">
                <h4>{item.name}</h4>
                <p>Qty: {item.quantity}</p>
              </div>
              <div className="item-price">₹{item.price * item.quantity}</div>
            </div>
          ))}

          <div className="pricing-breakdown">
            <div className="price-row">
              <span>Subtotal</span>
              <span>₹{totalAmount}</span>
            </div>
            {discountAmount && discountAmount > 0 ? (
              <div className="price-row discount">
                <span>Total Discount</span>
                <span>₹{discountAmount}</span>
              </div>
            ) : (
              ""
            )}

            <div className="price-row">
              <span>Shipping</span>
              <span>
                {deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
              </span>
            </div>
            <div className="price-row total">
              <span>To Pay</span>
              <span>₹{payableAmount}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`checkout-container ${!auth?.user?._id ? "one_grid" : ""}`}
      >
        {auth?.user?._id ? (
          <div className="checkout-main">
            <div className="section delivery-section">
              <h2>
                <MapPin size={20} />
                DELIVERY DETAILS
              </h2>

              <div className="delivery-info">
                {auth?.user?.addresses?.map((address) => (
                  <>
                    <div
                      key={address._id}
                      onClick={() => setSelectAddress(address?._id)}
                      className={`delivery-address ${
                        selectAddress === address?._id && "active"
                      }`}
                    >
                      <button
                        className="edit"
                        onClick={() => handleEditAddress(address)}
                      >
                        <SquarePen />
                      </button>
                      <div className="address-header">
                        <span className="deliver-to">
                          Deliver To {address?.fullName}
                        </span>
                      </div>
                      <div className="address-details">
                        <p>{address?.fullAddress}</p>
                        <p>
                          {address?.city}, {address?.state},{" "}
                          {address?.postalCode}
                        </p>
                        <p>
                          +91 {address?.addressPhone} |{address?.email}
                        </p>
                      </div>
                    </div>
                    {editAddress && editAddressId === address?._id && (
                      <div className="new-address-form update_address">
                        <p>Update Address Details</p>
                        <div className="form-grid">
                          <CustomInput
                            type="email"
                            name="email"
                            placeholder="Enter email"
                            value={addressDetails.email}
                            onChange={handleInputChange}
                            icon={<Mail size={16} />}
                          />
                          <CustomInput
                            type="text"
                            name="recieverName"
                            onChange={handleInputChange}
                            value={addressDetails?.recieverName}
                            placeholder="Enter reciver name"
                            icon={<User size={16} />}
                          />
                          <CustomInput
                            type="text"
                            name="addressPhone"
                            onChange={handleInputChange}
                            value={addressDetails?.addressPhone}
                            placeholder="Enter phone number (Do not add +1/0/91)"
                            icon={<Phone size={16} />}
                          />
                          <CustomInput
                            type="number"
                            name="postalCode"
                            ref={handlePostalCodeBlur}
                            onChange={handleInputChange}
                            value={addressDetails?.postalCode}
                            placeholder="Enter postal code"
                          />
                          <CustomInput
                            type="text"
                            name="fullAddress"
                            onChange={handleInputChange}
                            value={addressDetails?.fullAddress}
                            placeholder="Enter your full address"
                          />
                          <CustomInput
                            type="text"
                            name="city"
                            onChange={handleInputChange}
                            value={addressDetails?.city}
                            placeholder="Enter your city"
                          />

                          <CustomInput
                            type="text"
                            name="state"
                            onChange={handleInputChange}
                            value={addressDetails?.state}
                            placeholder="Enter state"
                          />

                          <CustomInput
                            type="text"
                            name="country"
                            onChange={handleInputChange}
                            value={addressDetails?.country}
                            placeholder="Enter country"
                          />
                        </div>
                        <button
                          className="save-address-btn"
                          onClick={handleUpdateAddress}
                          disabled={updateProfileLoading}
                        >
                          {updateProfileLoading
                            ? "Saving..."
                            : "Update Address"}
                        </button>
                      </div>
                    )}
                  </>
                ))}

                <div className="delivery-option">
                  <div className="delivery-method">
                    <Truck size={18} />
                    <div>
                      <span className="method-title">Delivery Option</span>
                      <div className="shipping-info">
                        <span className="shipping-type">Shipping</span>
                        <span className="shipping-cost">Free</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add New Address Form */}
              {addAddress && (
                <div className="new-address-form">
                  <p>Add Address</p>
                  <div className="form-grid">
                    <CustomInput
                      type="email"
                      name="email"
                      placeholder="Enter email"
                      value={addressDetails.email}
                      onChange={handleInputChange}
                      icon={<Mail size={16} />}
                    />
                    <CustomInput
                      type="text"
                      name="recieverName"
                      onChange={handleInputChange}
                      value={addressDetails?.recieverName}
                      placeholder="Enter reciver name"
                      icon={<User size={16} />}
                    />
                    <CustomInput
                      type="text"
                      name="addressPhone"
                      onChange={handleInputChange}
                      value={addressDetails?.addressPhone}
                      placeholder="Enter phone number (Do not add +1/0/91)"
                      icon={<Phone size={16} />}
                    />
                    <CustomInput
                      type="number"
                      name="postalCode"
                      ref={handlePostalCodeBlur}
                      onChange={handleInputChange}
                      value={addressDetails?.postalCode}
                      placeholder="Enter postal code"
                    />
                    <CustomInput
                      type="text"
                      name="fullAddress"
                      onChange={handleInputChange}
                      value={addressDetails?.fullAddress}
                      placeholder="Enter your full address"
                    />
                    <CustomInput
                      type="text"
                      name="city"
                      onChange={handleInputChange}
                      value={addressDetails?.city}
                      placeholder="Enter your city"
                    />

                    <CustomInput
                      type="text"
                      name="state"
                      onChange={handleInputChange}
                      value={addressDetails?.state}
                      placeholder="Enter state"
                    />

                    <CustomInput
                      type="text"
                      name="country"
                      onChange={handleInputChange}
                      value={addressDetails?.country}
                      placeholder="Enter country"
                    />
                  </div>
                  <button
                    className="save-address-btn"
                    onClick={handleSaveAddress}
                    disabled={addressLoading}
                  >
                    {addressLoading ? "Saving..." : "Save Address"}
                  </button>
                </div>
              )}

              {!addAddress && (
                <button className="add-address-btn" onClick={handleAddAddress}>
                  <Plus size={16} />
                  Add New Address
                </button>
              )}
            </div>

            {/* Offers & Rewards */}
            <div className="section offers-section">
              <div className="offer_title">
                <h2>
                  <Gift size={20} />
                  OFFERS & REWARDS
                </h2>

                <button
                  className="view-coupons"
                  onClick={() => handleViewCoupons()}
                >
                  {couponsLoading
                    ? "Loading..."
                    : showAvailableCoupons
                    ? "Hide coupons"
                    : "View all coupons"}
                </button>
              </div>

              {discountAmount !== 0 && (
                <div className="savings-banner">
                  You saved ₹{discountAmount}
                </div>
              )}

              <div className="coupon-section">
                <div className="coupon-input-wrapper">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      setDiscountAmount(0);
                      if (e.target.value !== lastCheckedCode) {
                        setIsCouponApplied(false);
                        setCouponError("");
                      }
                    }}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={isCouponApplied || !couponCode.trim()}
                  >
                    {couponLoading ? (
                      <ButtonLoader />
                    ) : isCouponApplied && !couponError ? (
                      "Applied"
                    ) : (
                      "Apply"
                    )}
                  </button>
                </div>

                {isCouponApplied && !couponError && couponCode !== "" && (
                  <div className="coupon-applied">
                    <div className="success-icon">✓</div>
                    <span>"{couponCode}" applied</span>
                  </div>
                )}

                
                {couponError && (
                  <div className="coupon-error">{couponError}</div>
                )}

                {showAvailableCoupons && (
                  <div className="available_coupons">
                    {availableCoupons?.length > 0 ? (
                      <div className="coupons_grid">
                        {availableCoupons.map((coupon) => {
                          const isApplicable = isCouponApplicable(coupon);
                          return (
                            <div
                              key={coupon.id}
                              className={`coupon-card ${
                                !isApplicable ? "disabled" : ""
                              }`}
                            >
                              <div className="coupon-left">
                                <div className="coupon-icon">
                                  <Gift />
                                </div>
                              </div>
                              <div className="coupon-right">
                                <div className="coupon-main">
                                  <div className="gift-header">
                                    {coupon?.title}
                                  </div>
                                  <div className="coupon-text">
                                    <span className="discount-amount">
                                      {coupon.discountPercent}% OFF
                                    </span>
                                    <span className="discount-label">
                                      {coupon?.code}
                                    </span>
                                  </div>
                                </div>
                                <div className="coupon-footer">
                                  {coupon?.toDate && (
                                    <div className="purchase-text">
                                      Expires:{" "}
                                      {moment(coupon?.toDate)?.format(
                                        "DD-MM-yyyy"
                                      )}
                                    </div>
                                  )}
                                  <div
                                    className={`coupon_status ${
                                      isApplicable
                                        ? "applicable"
                                        : "not_applicable"
                                    }`}
                                  >
                                    {getStatusText(coupon, isApplicable)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no_coupons">
                        <p>No coupons available at the moment</p>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="section payment-section">
              <h2>Payment Method</h2>
              <div className="payment-options">
                <label className="payment-option">
                  <input
                    type="radio"
                    name="paymentType"
                    value="cod"
                    checked={paymentType === "cod"}
                    onChange={(e) => setPaymentType(e.target.value)}
                  />
                  <span>Cash On Delivery</span>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="paymentType"
                    value="online"
                    checked={paymentType === "online"}
                    onChange={(e) => setPaymentType(e.target.value)}
                  />
                  <span>Pay Online</span>
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="checkout_login">
            <h1 className="login_title">
              Login to <span>Saaj Farms</span>
            </h1>

            {!otpSent ? (
              <>
                <input
                  className="input_text"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={mobile}
                  placeholder="Enter 10 digit mobile number"
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  maxLength={10}
                />
                <CustomButton
                  onClick={handleSendOtp}
                  width="100%"
                  className="login_btn"
                  title={loading ? <ButtonLoader /> : "Continue"}
                />
              </>
            ) : (
              <>
                <label>Enter OTP</label>
                <div className="otp_container">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputsRef.current[i] = el)}
                      className="otp_box"
                      id={`otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(e, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      onPaste={(e) => handlePaste(e, i)}
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>

                <CustomButton
                  onClick={handleVerify}
                  width="100%"
                  className="login_btn"
                  title={loading ? "Verifying..." : "Verify OTP"}
                  disabled={loading}
                />

                <div style={{ marginTop: 10, textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    style={{
                      background: "none",
                      border: "none",
                      color: "rgb(251 121 116)",
                      cursor: loading ? "not-allowed" : "pointer",
                      textDecoration: "underline",
                      fontSize: "14px",
                    }}
                  >
                    {loading ? "Resending..." : "Resend OTP"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Desktop Order Summary Sidebar */}
        <div
          className={`order-summary-desktop ${
            !auth?.user?._id ? "full_width" : ""
          }`}
        >
          <div className="summary-header">
            <div className="brand-info">
              <div className="brand-logo">
                <img src="/favicon.ico" alt="saaj_logo" />
              </div>
              <span>Saaj Farms</span>
            </div>
            <div className="summary-stats">
              {discountAmount && discountAmount > 0 ? (
                <span className="savings">₹{discountAmount} saved so far</span>
              ) : (
                ""
              )}
              <span className="item-count">{allItems.length} items</span>
              <div className="total">₹{payableAmount}</div>
            </div>
          </div>

          <div className="summary-items">
            {allItems.map((item) => (
              <div key={item._id} className="summary-item">
                <img
                  src={process.env.REACT_APP_FILE_PATH + "/" + item?.image}
                  alt={item.name}
                  className="item-image"
                />
                <div className="item-info">
                  <h4>{item.name}</h4>
                  <p>Qty: {item.quantity}</p>
                </div>
                <div className="item-price">₹{item.price * item.quantity}</div>
              </div>
            ))}
          </div>

          <div className="summary-breakdown">
            <div className="breakdown-row">
              <span>Subtotal</span>
              <span>₹{totalAmount}</span>
            </div>
            {discountAmount !== 0 && (
              <div className="breakdown-row discount">
                <span>Total Discount</span>
                <span>₹{discountAmount}</span>
              </div>
            )}
            <div className="breakdown-row">
              <span>Shipping</span>
              <span>
                {deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
              </span>
            </div>
            <div className="breakdown-row total">
              <span>To Pay</span>
              <span>₹{payableAmount}</span>
            </div>
          </div>

          {auth?.user?._id && (
            <button
              className="place-order-btn"
              onClick={handlePlaceOrder}
              disabled={
                profileLoading ||
                selectAddress === null ||
                (couponCode.trim() !== "" &&
                  (!isCouponApplied ||
                    couponError !== "" ||
                    lastCheckedCode !== couponCode))
              }
            >
              {paymentLoading ? <ButtonLoader /> : "Place Order"}
            </button>
          )}
        </div>
      </div>

      {/* Place Order Button */}
      {auth?.user?._id && (
        <div className="place-order-section">
          <button
            className="place-order-btn"
            onClick={handlePlaceOrder}
            disabled={
              profileLoading ||
              selectAddress === null ||
              (couponCode.trim() !== "" &&
                (!isCouponApplied ||
                  couponError !== "" ||
                  lastCheckedCode !== couponCode))
            }
          >
            {paymentLoading ? <ButtonLoader /> : "Place Order"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Checkout;
